import { useState } from "react";
import { motion } from "framer-motion";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useLang } from "@/i18n/LanguageContext";
import { LINES, ALERTS, HOURS, lineEfficiency, pick } from "@/demo/data";

const effColor = (e) => (e >= 95 ? "var(--mint)" : e >= 85 ? "var(--cyan)" : "#ff9c6b");
const levelColor = { high: "#ff9c6b", mid: "var(--cyan)", low: "var(--mint)" };

function LineRow({ line, active, onSelect }) {
  const { t } = useLang();
  const eff = lineEfficiency(line);
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      data-testid={`mgr-line-${line.id}`}
      className={`grid w-full grid-cols-[3.5rem_1fr_5rem] items-center gap-3 border-t border-line py-4 text-left transition-colors duration-300 md:grid-cols-[3.5rem_1.6fr_1fr_4.5rem_4.5rem_5rem] ${active ? "bg-[var(--navy-2)]" : "hover:bg-[var(--navy-1)]"}`}
    >
      <span className="mono pl-3 text-sm text-[var(--warm)]">{line.id}</span>
      <div className="min-w-0">
        <p className="truncate text-sm text-[var(--text)]">{line.style}</p>
        <div className="mt-2 h-1 w-full overflow-hidden bg-[var(--navy-3)]">
          <motion.i className="block h-full origin-left" style={{ background: effColor(eff) }} initial={{ scaleX: 0 }} animate={{ scaleX: Math.min(1, line.outputToday / line.targetToday) }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} />
        </div>
      </div>
      <span className="mono hidden text-xs text-[var(--muted)] md:block">
        {line.outputToday.toLocaleString()} <span className="text-[var(--muted-2)]">/ {line.targetToday.toLocaleString()}</span>
      </span>
      <span className="mono hidden text-xs text-[var(--muted)] md:block">
        {t("demoWip")} {line.wip}
      </span>
      <span className="mono hidden text-xs text-[var(--muted)] md:block">
        {t("demoDhu")} {line.dhu}
      </span>
      <span className="mono pr-3 text-right text-base font-semibold" style={{ color: effColor(eff) }}>
        {eff}%
      </span>
    </button>
  );
}

function ChartTip({ active, payload, label, t }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass p-3 text-xs">
      <p className="mono text-[var(--muted-2)]">{label}:00</p>
      <p className="mt-1 text-[var(--cyan)]">
        {t("demoActual")}: <b className="mono">{payload[0]?.value}</b>
      </p>
      <p className="text-[var(--muted)]">
        {t("demoTarget")}: <b className="mono">{payload[1]?.value}</b>
      </p>
    </div>
  );
}

export function ManagerView() {
  const { t, lang } = useLang();
  const [sel, setSel] = useState("L3");
  const line = LINES.find((l) => l.id === sel);
  const eff = lineEfficiency(line);
  const data = HOURS.map((h, i) => ({ h, actual: line.hourly[i] || null, target: line.targetHr }));
  const totals = LINES.reduce((a, l) => ({ out: a.out + l.outputToday, tgt: a.tgt + l.targetToday, wip: a.wip + l.wip, ops: a.ops + l.operators }), { out: 0, tgt: 0, wip: 0, ops: 0 });

  return (
    <div className="grid gap-6 lg:grid-cols-[1.25fr_1fr]" data-testid="manager-view">
      {/* lines table */}
      <div className="surface flex flex-col overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <span className="overline">{t("demoLines")}</span>
          <div className="flex gap-6 text-xs text-[var(--muted)]">
            <span>
              {t("demoOutputVsTarget")} <b className="mono text-[var(--warm)]">{Math.round((totals.out / totals.tgt) * 100)}%</b>
            </span>
            <span>
              {t("demoWip")} <b className="mono text-[var(--warm)]">{totals.wip.toLocaleString()}</b>
            </span>
            <span>
              {t("demoOperators")} <b className="mono text-[var(--warm)]">{totals.ops}</b>
            </span>
          </div>
        </div>
        <div className="flex flex-col">
          {LINES.map((l) => (
            <LineRow key={l.id} line={l} active={l.id === sel} onSelect={() => setSel(l.id)} />
          ))}
        </div>
        <div className="border-t border-line p-4 text-xs text-[var(--muted-2)]">{t("demoDhuFull")} · {t("demoNote")}</div>
      </div>

      {/* detail + alerts */}
      <div className="flex flex-col gap-6">
        <div className="surface p-6" data-testid="mgr-line-detail">
          <div className="flex items-start justify-between">
            <div>
              <span className="label">{t("demoLineDetail")}</span>
              <p className="display display-md mt-2 text-[var(--warm)]">{line.name}</p>
              <p className="mt-1 text-sm text-[var(--muted)]">{line.style}</p>
            </div>
            <div className="text-right">
              <span className="label">{t("demoEfficiency")}</span>
              <p className="mono mt-2 text-3xl font-semibold" style={{ color: effColor(eff) }} data-testid="mgr-line-eff">
                {eff}%
              </p>
            </div>
          </div>

          <div className="mt-6 h-44" data-testid="mgr-hourly-chart">
            <span className="label">{t("demoHourly")}</span>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 12, right: 4, left: -22, bottom: 0 }}>
                <defs>
                  <linearGradient id="mgr-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#1EC8FF" stopOpacity={0.45} />
                    <stop offset="1" stopColor="#1EC8FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(190,218,230,.08)" vertical={false} />
                <XAxis dataKey="h" tick={{ fill: "#607B89", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#607B89", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTip t={t} />} cursor={{ stroke: "rgba(190,218,230,.25)" }} />
                <Area type="monotone" dataKey="actual" stroke="#1EC8FF" strokeWidth={2} fill="url(#mgr-fill)" connectNulls={false} isAnimationActive />
                <Area type="stepAfter" dataKey="target" stroke="#16E6B2" strokeDasharray="4 6" strokeWidth={1.5} fill="none" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-px bg-[var(--line)]">
            <div className="bg-[var(--navy)] p-3">
              <span className="label">{t("demoWip")}</span>
              <p className="mono mt-1 text-lg text-[var(--warm)]">{line.wip}</p>
            </div>
            <div className="bg-[var(--navy)] p-3">
              <span className="label">{t("demoDhu")}</span>
              <p className="mono mt-1 text-lg text-[var(--warm)]">{line.dhu}</p>
            </div>
            <div className="bg-[var(--navy)] p-3">
              <span className="label">{t("demoOperators")}</span>
              <p className="mono mt-1 text-lg text-[var(--warm)]">{line.operators}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-line pt-4 text-sm">
            <span className="label">{t("demoBottleneck")}</span>
            <span className="text-[var(--text)]" data-testid="mgr-bottleneck">
              {pick(line.bottleneck, lang)}
            </span>
          </div>
        </div>

        <div className="surface p-6" data-testid="mgr-alerts">
          <div className="flex items-center justify-between">
            <span className="overline text-[var(--cyan)]">{t("demoAlerts")}</span>
            <i className="pulse-dot h-2 w-2 rounded-full bg-[var(--cyan)]" />
          </div>
          <ul className="mt-4 divide-y divide-[var(--line)]">
            {ALERTS.map((a) => (
              <li key={a.id} className="grid grid-cols-[auto_1fr_auto] items-start gap-3 py-3 text-sm">
                <i className="mt-1.5 h-2 w-2 rounded-full" style={{ background: levelColor[a.level] }} />
                <button type="button" onClick={() => setSel(a.line)} className="text-left text-[var(--text)] transition-colors hover:text-[var(--mint)]">
                  {pick(a.text, lang)}
                </button>
                <span className="mono text-xs text-[var(--muted-2)]">{a.time}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
